/*
 * Based on three.js DepthLimitedBlurShader.js
 */
import * as THREE from "three";
import type {
  ScreenSpaceViewNormalDefines,
  ScreenSpaceViewNormalUniforms,
  ScreenSpaceViewPositionDefines,
  ScreenSpaceViewPositionUniforms,
} from "./screenSpaceHelpersGLSL";
import {
  screenSpaceViewNormalDefineDefaults,
  screenSpaceViewNormalGLSL,
  screenSpaceViewNormalUniformDefaults,
  screenSpaceViewPositionDefineDefaults,
  screenSpaceViewPositionGLSL,
  screenSpaceViewPositionUniformDefaults,
} from "./screenSpaceHelpersGLSL";
import { UVVertexShader } from "./UVVertexShader";

interface DepthLimitedPoissonBlurDefines extends ScreenSpaceViewPositionDefines, ScreenSpaceViewNormalDefines {
  KERNEL_RADIUS: number; // eslint-disable-line @typescript-eslint/naming-convention
}

interface DepthLimitedPoissonBlurUniforms extends ScreenSpaceViewPositionUniforms, ScreenSpaceViewNormalUniforms {
  tDiffuse: THREE.IUniform;

  invSize: THREE.IUniform;

  samplePixelOffsets: THREE.IUniform;
  colorMask: THREE.IUniform;
}

interface DepthLimitedPoissonBlurShaderType {
  defines: DepthLimitedPoissonBlurDefines;
  uniforms: DepthLimitedPoissonBlurUniforms;
  vertexShader: string;
  fragmentShader: string;
}

export const DepthLimitedPoissonBlurShader: DepthLimitedPoissonBlurShaderType = {
  defines: {
    ...screenSpaceViewPositionDefineDefaults,
    ...screenSpaceViewNormalDefineDefaults,
    KERNEL_RADIUS: 6,
  },
  uniforms: {
    ...screenSpaceViewPositionUniformDefaults,
    ...screenSpaceViewNormalUniformDefaults,
    tDiffuse: { value: null },

    // Inverse of render target size in pixels - can also be thought as the size of one pixel in UV space.
    invSize: { value: new THREE.Vector2(1 / 512, 1 / 512) },

    samplePixelOffsets: { value: [new THREE.Vector2(0, 0)] },

    colorMask: { value: [true, true, true, true] },
  },
  vertexShader: UVVertexShader,
  fragmentShader: `#include <common>
    #include <packing>

    ${screenSpaceViewNormalGLSL}

    ${screenSpaceViewPositionGLSL}

    uniform sampler2D tDiffuse;

    uniform vec2 samplePixelOffsets[KERNEL_RADIUS + 1];

    uniform bvec4 colorMask;

    varying vec2 vUv;
    uniform vec2 invSize;

    void main() {
      float depth = GET_DEPTH(tDepth, vUv);
      if(IS_DEPTH_OUT_OF_BOUNDS(depth)) {
        discard;
      }
      vec3 centerViewPos = getViewPosition(vUv, getViewZ(depth));

      vec3 centerNormal = GET_VIEW_NORMAL(centerViewPos, vUv);

      float weightSum = 1.0;
      vec4 centerColor = texture2D(tDiffuse, vUv);
      vec4 diffuseSum = centerColor * weightSum;

      float centerOcc = diffuseSum.a;

      float angle = rand(vUv) * 6.28;
      mat2 rotationMatrix = mat2(cos(angle), -sin(angle), sin(angle), cos(angle));

      for(int i = 1; i <= KERNEL_RADIUS; i++) {
        vec2 offset = (rotationMatrix * samplePixelOffsets[i]) * invSize;
        vec2 sampleUv = vUv + offset;
        vec4 diffuseSample = texture2D(tDiffuse, sampleUv);
        float sampleOcc = diffuseSample.a;

        float sampleDepth = GET_DEPTH(tDepth, sampleUv);
        vec3 viewPosSample = getViewPosition(sampleUv, getViewZ(sampleDepth));
        vec3 normalSample = GET_VIEW_NORMAL(viewPosSample, sampleUv);
        float tangentPlaneDist = abs(dot(centerViewPos - viewPosSample, centerNormal));
        float weight = exp(-1.0 * tangentPlaneDist) * (0.5 + 0.5 * dot(centerNormal, normalSample)) * (1.0 - abs(sampleOcc - centerOcc));
        diffuseSum += diffuseSample * weight;
        weightSum += weight;
      }

      gl_FragColor = centerColor * vec4(not(colorMask)) + diffuseSum / weightSum * vec4(colorMask);
    }`,
} as const;

export const PoissonBlurShaderUtils = {
  createSampleOffsets: function (kernelRadius) {
    const offsets: THREE.Vector2[] = [];

    let angle = 0;

    for (let i = 0; i <= kernelRadius; i++) {
      angle += Math.PI * 0.77;
      const radius = i;
      offsets.push(new THREE.Vector2(radius * Math.cos(angle), radius * Math.sin(angle)));
    }

    return offsets;
  },

  configureKernel: function (material, kernelRadius) {
    if (material.defines["KERNEL_RADIUS"] !== kernelRadius) {
      material.defines["KERNEL_RADIUS"] = kernelRadius;
      material.needsUpdate = true;
    }
    material.uniforms["samplePixelOffsets"].value = PoissonBlurShaderUtils.createSampleOffsets(kernelRadius);
  },
};
